import { useState, useEffect, useRef } from 'react';
import { ProTable } from '@ant-design/pro-table';
import { Select, DatePicker, theme, Space, Tooltip, Typography, Input, App, Button } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
    useGetAdvancePaymentQuery,
    useGetCustomerUidWithBillingCycleQuery,
    useGetUserQuery,
    useDownloadAdvancePaymentDataMutation,
} from '../../features/api/apiSlice';
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;
function AdvancePaymentDetailsTab({ selectedBankSlip }) {
    const { token } = theme.useToken();
    const [searchData, setSearchData] = useState({});
    const { data, error, isLoading, isFetching, refetch } = useGetAdvancePaymentQuery({...searchData});
    const { data: customerUidData } = useGetCustomerUidWithBillingCycleQuery('预充');
    const { data: kaUserData, error: kaUserError, isLoading: kaUserIsLoading, refetch: refetchKaUserData } = useGetUserQuery();
    const { notification } = App.useApp();
    const formRef = useRef();
    const [downloadAdvancePaymentData, {
        isLoading: downloadAdvancePaymentDataIsLoading,
        isSuccess: downloadAdvancePaymentDataIsSuccess,
        isError: downloadAdvancePaymentDataIsError,
        error: downloadAdvancePaymentDataError,
    }] = useDownloadAdvancePaymentDataMutation();
    useEffect(() => {
        if (downloadAdvancePaymentDataIsSuccess) {
            notification.success({
                message: '下载成功',
                description: '下载成功, 请在后台任务管理页面查看文件',
            });
        }
        if (downloadAdvancePaymentDataIsError) {
            notification.error({
                message: '下载失败',
                description: downloadAdvancePaymentDataError?.data?.message || '未知错误',
            });
        }
    }, [downloadAdvancePaymentDataIsSuccess, downloadAdvancePaymentDataIsError]);
    useEffect(() => {
        if (selectedBankSlip && formRef.current) {
            formRef.current.setFieldsValue({ slip_name: selectedBankSlip });
            formRef.current.submit();
        }
    }, [selectedBankSlip]);
    const columns = [
        {
            title: 'UID',
            dataIndex: 'uid',
            key: 'uid',
            search: true,
            renderFormItem: (item, { defaultRender, ...rest }, form) => {
                return <Select
                    showSearch
                    allowClear
                    placeholder="Select a UID"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    mode="multiple"
                    style={{ textAlign: "left" }}
                >
                    {customerUidData && customerUidData.map((item) => {
                        return <Option key={item.uid} value={item.id}>{item.uid}</Option>
                    })}
                </Select>
            }
        },
        {
            title: '客户名',
            dataIndex: 'customer_name',
            key: 'customer_name',
            search: false,
        },
        {
            title: '系统',
            dataIndex: 'system',
            key: 'system',
            search: false,
        },
        {
            title: () => {
                return <Space>
                    <Text>水单名</Text>
                    <Tooltip style={{ marginLeft: '2px' }} title="系统自动生成；生成逻辑：当天日期+流水号"><QuestionCircleOutlined /></Tooltip>
                </Space>
            },
            dataIndex: 'slip_name',
            key: 'slip_name',
            search: true,
            renderFormItem: (item, { defaultRender, ...rest }, form) => {
                return <Input placeholder="请输入(支持模糊查询)" />
            }
        },
        {
            title: '支付金额',
            dataIndex: 'payment_amount',
            key: 'payment_amount',
            search: false,
            render: (text, record) => {
                return <div>{text} {record.currency}</div>
            }
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            search: false,
        },
        {
            title: '是否已到账',
            dataIndex: 'is_issued',
            key: 'is_issued',
            search: false,
            render: (text, record) => {
                return <div>{text === 1 ? '是' : '否'}</div>
            }
        },
        {
            title: '新建人',
            dataIndex: 'issuer',
            key: 'issuer',
            search: true,
            renderFormItem: (item, { defaultRender, ...rest }, form) => {
                return <Select
                    showSearch
                    allowClear
                    placeholder="请选择"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ textAlign: "left" }}
                >
                    {kaUserData && kaUserData.map((item) => {
                        return <Option key={item.id} value={item.id}>{item.username}</Option>
                    })}
                </Select>
            }
        },
        {
            title: '新建时间',
            dataIndex: 'issue_time',
            key: 'issue_time',
            search: true,
            renderFormItem: (item, { defaultRender, ...rest }, form) => {
                return <RangePicker />
            }
        },
        {
            title: '汇款日期',
            dataIndex: 'confirm_date',
            key: 'confirm_date',
            search: false,
        },
        {
            title: '回款类型',
            dataIndex: 'slip_type',
            key: 'slip_type',
            search: false,
        }
    ]

    const downloadButtonHandler = (searchConfig, formProps) => {
        searchConfig.form.validateFields().then((values) => {
            downloadAdvancePaymentData({...values});
        })
    }


    return (
        <ProTable
            rowKey={(record) => record.id}
            search={{
                labelWidth: 'auto',
                defaultCollapsed: false,
                style: {
                    maxWidth: 'none',
                    background: token.colorFillAlter,
                    borderRadius: token.borderRadiusLG,
                    padding: 24,
                },
                optionRender: (searchConfig, formProps, dom) => [
                    <Button key="downloadButton" type="primary"
                        loading={downloadAdvancePaymentDataIsLoading}
                        onClick={() => downloadButtonHandler(searchConfig, formProps)}
                    >
                        下载
                    </Button>,
                    ...dom
                ]
            }}
            columns={columns}
            dataSource={data?.results}
            request={(params) => {
                setSearchData(params);
            }}
            formRef={formRef}
            manualRequest={true}
            pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                total: data?.count,
            }}
            loading={isLoading || isFetching}
            scroll={{ x: 'max-content' }}
            options={{
                reload: () => refetch(),
            }}
        />
    )
}

export default AdvancePaymentDetailsTab;